/* eslint-disable react/function-component-definition */
import { Query } from "@apollo/client/react/components";
import classNames from "components/ui/classNames";
import FooterDataDisclaimer from "components/company/FooterDataDisclaimer";
import IndustrySector from "components/company/IndustrySector";
import NotFound from "components/errors/NotFound";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import { palette } from "lib/miolo";
import ValuationWallForTickerSymbolQuery from "lib/queries/ValuationWallForTickerSymbolQuery";
import type {
  IValuationWallForTickerSymbolQuery,
  IValuationWallForTickerSymbolQueryVariables,
} from "lib/queries/__generated__/ValuationWallForTickerSymbolQuery.generated";
import TickerSymbol from "lib/securities/TickerSymbol";
import type { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import ClipLoader from "react-spinners/ClipLoader";
import type { IValuationIndicator } from "types/graphqlTypes";
import {
  IAssetType,
  ISecurityCountry,
  IValuationIndicatorCategory,
  IValuationIndicatorVerbiageLanguage,
} from "types/graphqlTypes";
import getBaseUrl from "lib/utils/getBaseUrl";
import type {
  IIssuerHeaderByTickerSymbolQuery,
  IIssuerHeaderByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerHeaderByTickerSymbolQuery.generated";
import IssuerHeaderByTickerSymbolQuery from "lib/queries/IssuerHeaderByTickerSymbolQuery";
import IndicatorSnapshot from "components/company/valuation/IndicatorSnapshot";
import SheetValuationHistory from "components/company/valuation/SheetValuationHistory";
import { useState } from "react";
interface IIssuerHeaderProps {
  issuer: NonNullable<IIssuerHeaderByTickerSymbolQuery["issuerByTickerSymbol"]>;
  tickerSymbol: string;
}

function IssuerHeader({ issuer, tickerSymbol }: IIssuerHeaderProps) {
  if (issuer.__typename === "BrazilianCompanyIssuer") {
    return (
      <div className="mt-4 space-y-4 divide-y divide-slate-100 rounded-md border-neutral-200 bg-white p-6 shadow">
        <div className="space-y-6">
          <div className="flex">
            <div className="max-w-5xl space-y-2">
              <div className="space-y-1">
                <Link
                  href="/br/[tickerSymbol]"
                  as={`/br/${issuer.tickerSymbolPrefix}`.toLowerCase()}
                  replace={true}
                  passHref={true}
                  className="inline-flex"
                >
                  <h1 className="m-0 p-0 text-3xl font-bold text-gray-800 hover:underline">{issuer.name}</h1>
                </Link>

                <div className="flex">
                  <IndustrySector assetType={IAssetType.BrazilianCompany} industrySector={issuer.sector} />
                </div>
              </div>
              <p className="mt-0 p-0 font-normal text-gray-500">{issuer.businessDescription}</p>
            </div>

            {issuer.standardLogo && (
              <Link
                href="/br/[tickerSymbol]"
                as={`/br/${issuer.tickerSymbolPrefix}`.toLowerCase()}
                replace={true}
                passHref={true}
                className="ml-auto"
              >
                <img
                  role="presentation"
                  src={issuer.standardLogo.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onMouseDown={(event) => event.preventDefault()}
                  width={158}
                  height={48}
                  alt={issuer.name}
                  draggable={false}
                  className="h-12 select-none object-contain object-right"
                />
              </Link>
            )}
          </div>

          <div className="flex space-x-4">
            {issuer.securities
              .filter((security) => {
                return !TickerSymbol.isUnitStockSymbol(security.tickerSymbol);
              })
              .map((security) => {
                const linkHref = `/br/${security.tickerSymbol.toLowerCase()}/valuation`;
                const linkAs = `/[country]/[tickerSymbol]/valuation`;
                const isActive = security.tickerSymbol === tickerSymbol.toUpperCase();
                return (
                  <Link
                    key={security.tickerSymbol}
                    as={linkHref}
                    href={linkAs}
                    className={classNames(
                      "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700",
                      "border border-transparent",
                      "hover:border-emerald-600",
                      classNames({
                        "bg-emerald-500 text-white": isActive,
                      }),
                    )}
                  >
                    {security.tickerSymbol}
                  </Link>
                );
              })}
          </div>
        </div>

        <div className="flex pt-4">
          <Link
            href="/br/[tickerSymbol]"
            as={`/br/${issuer.tickerSymbolPrefix}`.toLowerCase()}
            replace={true}
            passHref={true}
            className="font-medium text-sky-700 hover:underline"
          >
            Visualizar Fundamentos
          </Link>
        </div>
      </div>
    );
  }
  if (issuer.__typename === "BrazilianRealEstateFund") {
    return (
      <div className="mt-4 space-y-4 divide-y divide-slate-100 rounded-md border-neutral-200 bg-white p-6 shadow">
        <div className="space-y-6">
          <div className="flex">
            <div className="max-w-5xl space-y-2">
              <div className="space-y-1">
                <Link
                  href="/fiis/[tickerSymbol]"
                  as={`/fiis/${issuer.tickerSymbolPrefix}11`.toLowerCase()}
                  replace={true}
                  passHref={true}
                  className="inline-flex"
                >
                  <h1 className="m-0 p-0 text-3xl font-bold text-gray-800 hover:underline">{issuer.name}</h1>
                </Link>
                <span className="text-md flex font-medium text-blue-600">{issuer.listingSegment}</span>
              </div>
              <p className="mt-0 p-0 font-normal text-gray-500">{issuer.shortDescription}</p>
            </div>

            {issuer.primaryLogo && (
              <Link
                href="/fiis/[tickerSymbol]"
                as={`/fiis/${issuer.tickerSymbolPrefix}11`.toLowerCase()}
                replace={true}
                passHref={true}
                className="ml-auto"
              >
                <img
                  role="presentation"
                  src={issuer.primaryLogo.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onMouseDown={(event) => event.preventDefault()}
                  width={158}
                  height={48}
                  alt={issuer.name}
                  draggable={false}
                  className="h-12 select-none object-contain object-right"
                />
              </Link>
            )}
          </div>

          <div className="flex space-x-4">
            <Link
              href="/fiis/[tickerSymbol]"
              as={`/fiis/${issuer.tickerSymbolPrefix}11`.toLowerCase()}
              replace={true}
              passHref={true}
              className={classNames(
                "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700",
                "border border-transparent",
                "hover:border-emerald-600",
                "bg-emerald-500 text-white",
              )}
            >
              {issuer.tickerSymbol}
            </Link>
          </div>
        </div>

        <div className="flex pt-4">
          <Link
            href="/fiis/[tickerSymbol]"
            as={`/fiis/${issuer.tickerSymbolPrefix}11`.toLowerCase()}
            replace={true}
            passHref={true}
            className="font-medium text-sky-700 hover:underline"
          >
            Visualizar Fundamentos
          </Link>
        </div>
      </div>
    );
  }
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return (
      <div className="mt-4 space-y-4 divide-y divide-slate-100 rounded-md border-neutral-200 bg-white p-6 shadow">
        <div className="space-y-6">
          <div className="flex">
            <div className="max-w-5xl space-y-2">
              <div className="space-y-1">
                <Link
                  href="/us/[tickerSymbol]"
                  as={`/us/${issuer.tickerSymbolPrefix}`.toLowerCase()}
                  replace={true}
                  passHref={true}
                  className="inline-flex"
                >
                  <h1 className="m-0 p-0 text-4xl font-medium text-gray-800">{issuer.name}</h1>
                </Link>

                <div>
                  <IndustrySector
                    assetType={issuer.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany}
                    industrySector={issuer.sector}
                  />
                </div>
              </div>

              <p className="mt-0 p-0 text-base font-normal text-gray-500">{issuer.shortBusinessDescription}</p>
            </div>

            {issuer.standardLogo && (
              <Link
                href="/us/[tickerSymbol]"
                as={`/us/${issuer.tickerSymbolPrefix}`.toLowerCase()}
                replace={true}
                passHref={true}
                className="ml-auto"
              >
                <img
                  role="presentation"
                  src={issuer.standardLogo.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onMouseDown={(event) => event.preventDefault()}
                  width={158}
                  height={48}
                  alt={issuer.name}
                  draggable={false}
                  className="h-12 select-none object-contain object-right"
                />
              </Link>
            )}
          </div>

          <div className="flex space-x-4">
            {issuer.securities.map((security) => {
              const linkHref = `/us/${security.tickerSymbol.toLowerCase()}/valuation`;
              const linkAs = `/[country]/[tickerSymbol]/valuation`;
              const isActive = security.tickerSymbol === tickerSymbol.toUpperCase();
              return (
                <Link
                  key={security.tickerSymbol}
                  as={linkHref}
                  href={linkAs}
                  className={classNames(
                    "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700",
                    "border border-transparent",
                    "hover:border-emerald-600",
                    classNames({
                      "bg-emerald-500 text-white": isActive,
                    }),
                  )}
                >
                  {security.tickerSymbol}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex pt-4">
          <Link
            href="/us/[tickerSymbol]"
            as={`/us/${issuer.tickerSymbolPrefix}`.toLowerCase()}
            replace={true}
            passHref={true}
            className="font-medium text-sky-700 hover:underline"
          >
            View Fundamentals
          </Link>
        </div>
      </div>
    );
  }
  return null;
}

interface IValuationByTickerSymbolProps {
  tickerSymbol: string | string[] | undefined;
  country: string | string[] | undefined;
  tab: string;
}

const ValuationByTickerSymbol: NextPage<IValuationByTickerSymbolProps> = ({ tickerSymbol, country, tab }) => {
  const [isIndicator, setIsIndicator] = useState<null | IValuationIndicator>(null);

  const viewer = useSelfie();
  const amplitude = useAmplitude();
  const normalizedTickerSymbol = Array.isArray(tickerSymbol) ? tickerSymbol[0] : tickerSymbol || "";
  const securityCountry = (
    Array.isArray(country) ? country[0].toUpperCase() : (country || "").toUpperCase()
  ) as ISecurityCountry;

  if (
    !tickerSymbol ||
    tickerSymbol.length > 8 ||
    /^([0-9]+)$/.test(normalizedTickerSymbol) ||
    ![ISecurityCountry.Br, ISecurityCountry.Us].includes(securityCountry)
  ) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={`${normalizedTickerSymbol.toUpperCase()} - Valuation | Fundamentei`} />

      <Query<IIssuerHeaderByTickerSymbolQuery, IIssuerHeaderByTickerSymbolQueryVariables>
        query={IssuerHeaderByTickerSymbolQuery}
        variables={{
          tickerSymbol: normalizedTickerSymbol,
          country: securityCountry,
        }}
      >
        {({ data, loading }) => {
          if (loading) {
            return (
              <div className="mt-8 text-center">
                <ClipLoader size={32} color={palette.royalBlue} loading={true} />
              </div>
            );
          }
          if (!data || !data.issuerByTickerSymbol) {
            return (
              <>
                <SetHttpStatusCode httpStatus={404} />
                <NotFound />
              </>
            );
          }

          const issuer = data.issuerByTickerSymbol;
          const verbiageLanguage =
            securityCountry === ISecurityCountry.Br
              ? IValuationIndicatorVerbiageLanguage.PtBr
              : IValuationIndicatorVerbiageLanguage.EnUs;

          return (
            <Query<IValuationWallForTickerSymbolQuery, IValuationWallForTickerSymbolQueryVariables>
              query={ValuationWallForTickerSymbolQuery}
              variables={{
                tickerSymbol: normalizedTickerSymbol,
                securityCountry,
                verbiageLanguage,
              }}
            >
              {({ data, loading }) => {
                if (loading || !data || !data.valuationWallForTickerSymbol) {
                  return null;
                }

                const nodes = data.valuationWallForTickerSymbol.filter((node) => {
                  if (tab === "MY_INDICATORS") {
                    return viewer && viewer.settings.bookmarkedIndicators.includes(node.indicator);
                  }
                  return node.category === tab;
                });

                return (
                  <div className="mx-auto max-w-7xl space-y-4 p-4 font-aeonik lg:p-0 lg:pb-4">
                    <ValuationHead issuer={issuer} tickerSymbol={normalizedTickerSymbol.toUpperCase()} />
                    <IssuerHeader issuer={issuer} tickerSymbol={normalizedTickerSymbol} />

                    <div>
                      <div className="relative flex w-full">
                        <ValuationTabs
                          tickerSymbol={normalizedTickerSymbol}
                          securityCountry={securityCountry}
                          selectedTab={tab}
                          availableCategories={data.valuationWallForTickerSymbol.map((node) => node.category)}
                          verbiageLanguage={verbiageLanguage}
                          bookmarkedIndicatorsCount={viewer ? viewer.settings.bookmarkedIndicators.length : 0}
                        />
                      </div>
                    </div>

                    {nodes.length > 0 && (
                      <div className="space-y-4">
                        <div
                          className={classNames(
                            "grid overflow-hidden rounded-md bg-white shadow",
                            "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4",
                            "border",
                            "p-1",
                            "divide divide-gray-100",
                          )}
                        >
                          {nodes.map((node) => {
                            return (
                              <SheetValuationHistory
                                key={node.indicator}
                                verbiageLanguage={verbiageLanguage}
                                securityCountry={securityCountry}
                                tickerSymbol={normalizedTickerSymbol}
                                indicator={node.indicator}
                                tab={tab}
                                issuer={issuer}
                                indicators={nodes}
                                viewerCanSeeHistory={node.viewerCanSeeHistory}
                                setIsOpen={(open) => {
                                  if (!node.viewerCanSeeHistory) {
                                    return;
                                  }

                                  setIsIndicator(open ? node.indicator : null);
                                }}
                                onSetIndicator={setIsIndicator}
                                isOpen={isIndicator === node.indicator}
                              >
                                <IndicatorSnapshot
                                  indicator={node.indicator}
                                  abbreviation={node.verbiage.abbreviation}
                                  description={node.verbiage.description}
                                  category={tab === "MY_INDICATORS" ? node.category : undefined}
                                  originalValue={node.value}
                                  verbiageLanguage={verbiageLanguage}
                                  formattedValue={node.formattedValue}
                                  isBookmarked={
                                    viewer ? viewer.settings.bookmarkedIndicators.includes(node.indicator) : false
                                  }
                                  viewerCanBookmark={viewer !== null}
                                  isAdjusted={String(node.indicator).includes("NORMALIZED")}
                                  viewerCanSeeHistory={node.viewerCanSeeHistory}
                                  onToggleActive={() => {
                                    amplitude.logEvent("Viewed a valuation indicator", {
                                      tickerSymbol: normalizedTickerSymbol.toUpperCase(),
                                      indicator: node.indicator,
                                      abbreviation: node.verbiage.abbreviation,
                                      category: node.category,
                                    });

                                    if (!node.viewerCanSeeHistory) {
                                      return;
                                    }

                                    window.history.pushState(
                                      null,
                                      "",
                                      `/${securityCountry.toLocaleLowerCase()}/${tickerSymbol}/valuation/${
                                        node.indicator
                                      }`,
                                    );

                                    setIsIndicator((previous) => {
                                      if (node.indicator === previous) {
                                        return null;
                                      }

                                      return node.indicator;
                                    });
                                  }}
                                />
                              </SheetValuationHistory>
                            );
                          })}
                        </div>

                        {issuer.__typename === "BrazilianCompanyIssuer" && issuer.sector !== "Bancos" && (
                          <div className="font-normal">
                            <FooterDataDisclaimer>
                              Os indicadores de <strong>EBIT</strong> e <strong>EBITDA</strong> da área de Valuation são
                              calculados pela Morningstar e podem sofrer algumas diferenças para os valores da tabela
                              fundamentalista.
                            </FooterDataDisclaimer>
                          </div>
                        )}
                      </div>
                    )}

                    {nodes.length === 0 && tab === "MY_INDICATORS" && (
                      <div className="space-y-1">
                        <h4 className="mt-2 text-2xl font-bold text-slate-700">Nenhum indicador encontrado.</h4>
                        <p className="mx-auto text-base font-normal tracking-wide text-gray-500">
                          Para simplificar sua análise, aqui você encontra todos os indicadores que você salvou. Volte
                          aqui quando você salvar algum—é uma aba dedicada apenas para você :)
                        </p>
                      </div>
                    )}

                    {nodes.length === 0 && tab !== "MY_INDICATORS" && (
                      <div className="space-y-1">
                        <h4 className="mt-2 text-2xl font-bold text-slate-700">Nenhum indicador encontrado.</h4>
                        <p className="mx-auto text-base font-normal tracking-wide text-gray-500">
                          Não encontramos nenhum indicador para <strong>{normalizedTickerSymbol.toUpperCase()}</strong>.
                          Verifique se o ticker foi digitado corretamente—ou se o mesmo está listado. Caso contrário,
                          não possuimos dados no momento para tal ativo :(
                        </p>
                      </div>
                    )}
                  </div>
                );
              }}
            </Query>
          );
        }}
      </Query>
    </NavbarLayout>
  );
};

ValuationByTickerSymbol.getInitialProps = async ({ query }) => ({
  tickerSymbol: Array.isArray(query.tickerSymbol) ? query.tickerSymbol[0] : query.tickerSymbol,
  country: Array.isArray(query.country) ? query.country[0] : query.country,
  tab: Array.isArray(query.tab) ? query.tab[0] : query.tab || IValuationIndicatorCategory.Valuation,
});

export default ValuationByTickerSymbol;

type TValuationTab = `${IValuationIndicatorCategory}` | "MY_INDICATORS";

interface IValuationTabsProps {
  tickerSymbol: string;
  securityCountry: ISecurityCountry;
  selectedTab: string;
  verbiageLanguage: IValuationIndicatorVerbiageLanguage;
  bookmarkedIndicatorsCount?: number;
  availableCategories: IValuationIndicatorCategory[];
}

function ValuationTabs({
  tickerSymbol,
  securityCountry,
  selectedTab,
  verbiageLanguage,
  bookmarkedIndicatorsCount = 0,
  availableCategories,
}: IValuationTabsProps) {
  const isEnUS = verbiageLanguage === IValuationIndicatorVerbiageLanguage.EnUs;
  const viewer = useSelfie();
  const router = useRouter();
  const amplitude = useAmplitude();
  const tabs: {
    id: TValuationTab;
    title: string;
    count: number;
  }[] = [
    {
      id: IValuationIndicatorCategory.Valuation,
      title: isEnUS ? "Valuation" : "Valuation",
      count: 0,
    },
    {
      id: IValuationIndicatorCategory.MarketCapitalization,
      title: isEnUS ? "Market Capitalization" : "Valor de Mercado",
      count: 0,
    },
    {
      id: IValuationIndicatorCategory.Earnings,
      title: isEnUS ? "Dividends" : "Proventos",
      count: 0,
    },
    {
      id: IValuationIndicatorCategory.Profitability,
      title: isEnUS ? "Profitability" : "Lucratividade",
      count: 0,
    },
    {
      id: IValuationIndicatorCategory.Growth,
      title: isEnUS ? "Growth" : "Crescimento",
      count: 0,
    },
    {
      id: IValuationIndicatorCategory.Leverage,
      title: isEnUS ? "Leverage" : "Alavancagem",
      count: 0,
    },
  ];
  if (viewer) {
    tabs.push({
      id: "MY_INDICATORS",
      title: isEnUS ? "My Indicators" : "Meus Indicadores",
      count: bookmarkedIndicatorsCount,
    });
  }

  const availableTabs = tabs.filter((tab) => {
    if (tab.id === "MY_INDICATORS") {
      return true;
    }
    return availableCategories.includes(tab.id as IValuationIndicatorCategory);
  });
  const linkAs = `/${securityCountry.toLowerCase()}/${tickerSymbol}/valuation`;
  const linkHref = `/[country]/[tickerSymbol]/valuation`;
  return (
    <>
      <select
        className="h-12 w-full rounded-md border border-gray-200 font-normal text-gray-600 lg:hidden"
        value={selectedTab}
        onChange={(event) => {
          amplitude.logEvent("Viewed a valuation tab", {
            tab: event.currentTarget.value,
          });

          const tabId = event.currentTarget.value;
          router.push(`${linkHref}?tab=${tabId}`, `${linkAs}?tab=${tabId}`);
        }}
      >
        {availableTabs.map(({ id, title, count }) => {
          return (
            <option key={id} value={id}>
              {count > 0 ? `${title} (${count})` : title}
            </option>
          );
        })}
      </select>

      <div className="hidden w-full border-b border-gray-200 lg:flex">
        <nav className="-mb-px flex space-x-6" aria-label="Abas">
          {availableTabs.map(({ id, title, count }) => {
            const tabId = id;
            const isSelected = selectedTab === id;

            return (
              <Link
                key={tabId}
                as={`${linkAs}?tab=${tabId}`}
                href={`${linkHref}?tab=${tabId}`}
                passHref={true}
                onClick={() => {
                  amplitude.logEvent("Viewed a valuation tab", {
                    tab: tabId,
                  });
                }}
                className={classNames(
                  "flex items-center",
                  "cursor-pointer select-none whitespace-nowrap py-3",
                  "font-normal text-gray-500",
                  "border-b-2 border-transparent",
                  classNames({
                    "border-teal-600 font-bold text-teal-600": isSelected,
                    "border-purple-600 font-bold text-purple-600": isSelected && id === "MY_INDICATORS",
                  }),
                )}
              >
                {title}
                {count > 0 && (
                  <span
                    className={classNames(
                      "inline-flex h-5 w-5 items-center justify-center",
                      "border-0.5 rounded-md border",
                      "text-xs font-bold",
                      "border-gray-400 bg-gray-50 text-gray-500",
                      classNames({
                        "border-teal-600 bg-teal-50 text-teal-700": isSelected,
                        "border-purple-500 bg-purple-50 text-purple-600": isSelected && id === "MY_INDICATORS",
                      }),
                      "ml-2",
                    )}
                  >
                    {count}
                  </span>
                )}
                {/* Just to avoid UI flick */}
                {id === "MY_INDICATORS" && count === 0 && <span className="h-5 w-0" />}
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
}

interface IValuationHeadProps {
  issuer: NonNullable<IIssuerHeaderByTickerSymbolQuery["issuerByTickerSymbol"]>;
  tickerSymbol: string;
}

function ValuationHead({ issuer, tickerSymbol }: IValuationHeadProps) {
  const gitShaShort = process.env.GIT_SHA_SHORT || "1";

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    const imageUrl = `${getBaseUrl()}/og/issuer/${tickerSymbol.toLowerCase()}?ns=br&valuation=true&v=${gitShaShort}`;
    return (
      <FundamenteiMetadata
        title={`${tickerSymbol} - Valuation | Fundamentei`}
        description={`${tickerSymbol} - ${issuer.name}: Principais indicadores de valuation (P/L, P/VPA, EBITDA, ROE), CAGR, Alavancagem (D/PL, Quick Ratio, Arrendamento) e muito mais!`}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `Dados de valuation de ${tickerSymbol} - ${issuer.name} no Fundamentei`,
            },
          ],
        }}
      />
    );
  }
  if (issuer.__typename === "AmericanCompanyIssuer") {
    const imageUrl = `${getBaseUrl()}/og/issuer/${tickerSymbol.toLowerCase()}?ns=us&valuation=true&v=${gitShaShort}`;
    return (
      <FundamenteiMetadata
        title={`${tickerSymbol} - Valuation | Fundamentei`}
        description={`${tickerSymbol} - ${issuer.name}: Principais indicadores de valuation (P/L, P/VPA, EBITDA, ROE), CAGR, Alavancagem (D/PL, Quick Ratio, Arrendamento) e muito mais!`}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `Dados de valuation de ${tickerSymbol} - ${issuer.name} no Fundamentei`,
            },
          ],
        }}
      />
    );
  }
  return null;
}
